import { HeadContent, Outlet, Scripts, createRootRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { Toaster } from 'sonner'
import Header from '../components/layout/Header'
import Footer from '../components/layout/Footer'
import { CmsProvider } from '../components/cms/CmsProvider'
import { initPostHog, posthog } from '../lib/posthog'
import { initSentry } from '../lib/sentry'
import ErrorBoundary from '../components/ErrorBoundary'
import { seoMeta } from '../lib/seo'
import { getSiteConfig } from '../lib/content'

import appCss from '../styles.css?url'

export const Route = createRootRoute({
  loader: async () => ({ config: await getSiteConfig() }),
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      ...seoMeta(),
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
      { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
      { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
      { rel: 'manifest', href: '/manifest.json' },
    ],
  }),
  component: RootComponent,
  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body className="font-sans antialiased">
        {children}
        <Scripts />
      </body>
    </html>
  )
}

function RootComponent() {
  const { config } = Route.useLoaderData()

  // Initialize analytics & error tracking
  useEffect(() => {
    initPostHog()
    initSentry()
  }, [])

  // Capture pageviews on route change
  const href = useRouterState({ select: (s) => s.location.href })
  useEffect(() => {
    posthog.capture?.('$pageview', { $current_url: `${window.location.origin}${href}` })
  }, [href])

  return (
    <CmsProvider>
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <Header orderUrl={config.orderOnlineUrl} />
      <ErrorBoundary>
        <div id="main-content">
          <Outlet />
        </div>
      </ErrorBoundary>
      <Footer />
      <Toaster position="top-center" richColors closeButton />
    </CmsProvider>
  )
}
